import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Select, Input, Button, Space, Popconfirm, Typography, message } from 'antd';
import { ReloadOutlined, DeleteOutlined, PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text } = Typography;
const { Search } = Input;

const CHANNEL_LABELS = {
  main: { label: 'عمومی', color: 'default' },
  Gryffindor: { label: 'گریفیندور', color: 'red' },
  Hufflepuff: { label: 'هافلپاف', color: 'gold' },
  Ravenclaw: { label: 'ریونکلاو', color: 'blue' },
  Slytherin: { label: 'اسلیترین', color: 'green' }
};

export default function ForumModerationManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ channel: undefined, q: '' });

  const fetchList = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, limit };
      if (filters.channel) params.channel = filters.channel;
      if (filters.q) params.q = filters.q;
      const { data } = await adminAPI.getForumPosts(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      message.error('خطا در دریافت پست‌های فروم');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters]);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteForumPost(id);
      message.success('پست حذف شد');
      fetchList(page);
    } catch (e) {
      message.error('حذف ناموفق بود');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      await adminAPI.togglePinForumPost(id);
      fetchList(page);
    } catch (e) {
      message.error('عملیات ناموفق بود');
    }
  };

  const columns = [
    { title: 'زمان', dataIndex: 'createdAt', width: 150, render: (v) => moment(v).format('YYYY-MM-DD HH:mm') },
    {
      title: 'کانال',
      dataIndex: 'channel',
      width: 120,
      render: (c) => {
        const meta = CHANNEL_LABELS[c] || { label: c, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      }
    },
    {
      title: 'نویسنده',
      dataIndex: 'author',
      width: 160,
      render: (a) => a ? <Text>{a.farsiFirstName || a.firstName} {a.farsiLastName || a.lastName}</Text> : '—'
    },
    { title: 'متن', dataIndex: 'content', render: (c) => c?.length > 120 ? `${c.slice(0, 120)}…` : c },
    {
      title: 'پین',
      dataIndex: 'isPinned',
      width: 70,
      render: (v) => v ? <PushpinFilled style={{ color: '#722f37' }} /> : null
    },
    {
      title: 'عملیات',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={record.isPinned ? <PushpinFilled /> : <PushpinOutlined />}
            onClick={() => handleTogglePin(record._id)}
          />
          <Popconfirm title="حذف این پست؟" onConfirm={() => handleDelete(record._id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="فیلترها"
        extra={<Button icon={<ReloadOutlined />} onClick={() => fetchList(page)}>بروزرسانی</Button>}
      >
        <Space wrap>
          <Select
            allowClear
            placeholder="کانال"
            style={{ width: 160 }}
            value={filters.channel}
            onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, channel: v })); }}
            options={Object.entries(CHANNEL_LABELS).map(([value, meta]) => ({ value, label: meta.label }))}
          />
          <Search
            placeholder="جستجو در متن پست..."
            style={{ width: 260 }}
            allowClear
            onSearch={(v) => { setPage(1); setFilters((f) => ({ ...f, q: v })); }}
          />
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ current: page, pageSize: limit, total, showSizeChanger: false, onChange: setPage }}
        />
      </Card>
    </div>
  );
}
